/**
 * Typed API client built on openapi-fetch.
 *
 * Auth: pulls the in-memory access token from `lib/auth.ts` and attaches it
 * to every request as `Authorization: Bearer …`. On 401 the middleware
 * spends the refresh token, retries once, and falls through with the failure
 * if the second attempt is also 401 — `<NeedsAuth>` then redirects to /login.
 *
 * `getClient()` returns null when the user isn't authenticated yet (initial
 * load before bootstrap, or after logout). Callers should wrap their data UI
 * in `<NeedsAuth>` so they don't render against null.
 */

import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './api.types';
import { getAccessToken, getSnapshot, refresh } from './auth';
import { getApiUrl } from './settings';

export type ApiClient = ReturnType<typeof createClient<paths>>;

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    // One refresh attempt per failed request. If refresh succeeds we replay;
    // if it doesn't, fall through with the original 401 and the calling
    // component can let `<NeedsAuth>` redirect.
    const ok = await refresh();
    if (!ok) return response;
    const retry = new Request(request, request);
    const token = getAccessToken();
    if (token) retry.headers.set('Authorization', `Bearer ${token}`);
    return fetch(retry);
  },
};

export function getClient(): ApiClient | null {
  if (getSnapshot().status !== 'authenticated') return null;
  const client = createClient<paths>({ baseUrl: getApiUrl() });
  client.use(authMiddleware);
  return client;
}

/**
 * Throw if not configured. Convenience wrapper for places that should
 * have already redirected if auth is missing.
 */
export function requireClient(): ApiClient {
  const c = getClient();
  if (!c) throw new Error('flowlet: not authenticated');
  return c;
}
